import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { isFeatureOn, WABA_FLAGS } from "../../helpers/featureFlags";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  overflowY: "auto",
  ...theme.scrollbarStyles,
}));

// cotação Meta (fatura em US$); mostramos uma estimativa em R$ ao lado
const USD_BRL = 5.6;

const StatCard = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ textAlign: "center", py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      {sub != null && (
        <Typography variant="caption" sx={{ color: color || "text.secondary" }}>{sub}</Typography>
      )}
    </CardContent>
  </Card>
);

const fmtDur = (sec) => {
  const s = Math.max(0, parseInt(sec, 10) || 0);
  const mm = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}m${ss}s`;
};

const fmtDay = (d) => {
  // dataPoint da Meta traz start (epoch em segundos) OU dia; tratamos os dois
  const ts = typeof d === "number" ? d * 1000 : Date.parse(d);
  if (Number.isNaN(ts)) return String(d);
  return new Date(ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const CallReport = () => {
  const [days, setDays] = useState(30);
  const [whatsappId, setWhatsappId] = useState("");
  const [connections, setConnections] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const aiOn = isFeatureOn(WABA_FLAGS.CALL_AI);

  const sentEmoji = (s) => {
    const v = (s || "").toLowerCase();
    if (v.startsWith("pos")) return "🙂";
    if (v.startsWith("neg")) return "😕";
    return "😐";
  };

  // só conexões WABA oficiais têm call_analytics
  useEffect(() => {
    const fetchConns = async () => {
      try {
        const { data } = await api.get("/whatsapp");
        const wabas = (Array.isArray(data) ? data : []).filter(
          (c) => c.isOfficialWaba || c.type === "waba"
        );
        setConnections(wabas);
        if (wabas.length && !whatsappId) setWhatsappId(String(wabas[0].id));
      } catch (err) {
        // silencioso
      }
    };
    fetchConns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    if (!whatsappId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/waba/${whatsappId}/call-analytics`, {
        params: { days },
      });
      setData(data?.data || null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    if (!aiOn) return;
    try {
      const { data } = await api.get("/voice/insights", { params: { days } });
      setInsights(data?.enabled ? data : null);
    } catch (err) {
      // silencioso — a inteligência é add-on; não atrapalha o relatório de voz
    }
  };

  useEffect(() => {
    if (whatsappId) fetchReport();
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappId]);

  const costUsd = data?.costUsd || 0;
  const costBrl = costUsd * USD_BRL;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Ligações (voz)</Title>
      </MainHeader>
      <MainPaper variant="outlined">
        {/* Filtros */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <TextField
            select label="Conexão" size="small" value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {connections.length === 0 && (
              <MenuItem value="">Nenhuma WABA oficial</MenuItem>
            )}
            {connections.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="Período" size="small" value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value={7}>Últimos 7 dias</MenuItem>
            <MenuItem value={30}>Últimos 30 dias</MenuItem>
            <MenuItem value={90}>Últimos 90 dias</MenuItem>
          </TextField>
          <Button variant="contained" onClick={() => { fetchReport(); fetchInsights(); }} disabled={loading || !whatsappId}>
            {loading ? <CircularProgress size={20} /> : "Gerar"}
          </Button>
        </Box>

        {/* Cards de totais */}
        {data && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}><StatCard label="Total de ligações" value={data.total} /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Recebidas" value={data.inbound} sub="grátis" color="#2e7d32" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Realizadas" value={data.outbound} color="#1565c0" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Duração média" value={fmtDur(data.avgDurationSec)} /></Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard
                label="Custo no período"
                value={`US$ ${costUsd.toFixed(2)}`}
                sub={`≈ R$ ${costBrl.toFixed(2)}`}
                color="#b26a00"
              />
            </Grid>
          </Grid>
        )}

        {/* Detalhamento por dia */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Detalhamento por dia
        </Typography>
        {data && (!data.dataPoints || data.dataPoints.length === 0) && (
          <Typography variant="body2" color="textSecondary">
            Nenhuma ligação registrada no período.
          </Typography>
        )}
        {data?.dataPoints?.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dia</TableCell>
                <TableCell align="center">Direção</TableCell>
                <TableCell align="center">Ligações</TableCell>
                <TableCell align="center">Duração média</TableCell>
                <TableCell align="right">Custo (US$)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.dataPoints.map((p, i) => {
                const inbound = p.direction === "USER_INITIATED";
                return (
                  <TableRow key={i}>
                    <TableCell>{fmtDay(p.start || p.date || p.day)}</TableCell>
                    <TableCell align="center" sx={{ color: inbound ? "#2e7d32" : "#1565c0" }}>
                      {inbound ? "Recebida" : "Realizada"}
                    </TableCell>
                    <TableCell align="center">{p.count || 0}</TableCell>
                    <TableCell align="center">{fmtDur(p.average_duration)}</TableCell>
                    <TableCell align="right">
                      {inbound ? "—" : (p.cost || 0).toFixed(4)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 2 }}>
          Ligações recebidas não têm custo. O custo das realizadas é faturado pela Meta em US$;
          o valor em R$ é uma estimativa (câmbio US$ 1 ≈ R$ {USD_BRL.toFixed(2)}).
        </Typography>

        {/* Inteligência de Chamadas (add-on) — só com a flag call-intelligence */}
        {aiOn && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>
              🧠 Inteligência de Chamadas (IA)
            </Typography>
            {insights && insights.summary.total > 0 ? (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}><StatCard label="Ligações analisadas" value={insights.summary.total} /></Grid>
                  <Grid item xs={6} md={3}><StatCard label="Satisfação média" value={insights.summary.csatAvg != null ? `${insights.summary.csatAvg}/5` : "—"} color="#1565c0" /></Grid>
                  <Grid item xs={6} md={3}><StatCard label="Positivas" value={insights.summary.sentiments.positivo} sub="🙂" color="#2e7d32" /></Grid>
                  <Grid item xs={6} md={3}><StatCard label="Negativas" value={insights.summary.sentiments.negativo} sub="😕" color="#c62828" /></Grid>
                </Grid>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Quando</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell align="center">Sentimento</TableCell>
                      <TableCell align="center">Satisf.</TableCell>
                      <TableCell>Motivo</TableCell>
                      <TableCell>Resumo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insights.items.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(r.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</TableCell>
                        <TableCell>{r.contactName || r.number || "—"}</TableCell>
                        <TableCell align="center">{sentEmoji(r.sentiment)} {r.sentiment || "—"}</TableCell>
                        <TableCell align="center">{r.csat ? `${r.csat}/5` : "—"}</TableCell>
                        <TableCell>{r.reason || "—"}</TableCell>
                        <TableCell sx={{ maxWidth: 380 }}>
                          <Typography variant="body2">{r.summary || "—"}</Typography>
                          {r.tags?.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {r.tags.map((t, i) => (
                                <Chip key={i} label={t} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Ainda não há ligações analisadas no período. Assim que uma ligação for gravada, a transcrição e o resumo aparecem aqui automaticamente.
              </Typography>
            )}
          </>
        )}
      </MainPaper>
    </MainContainer>
  );
};

export default CallReport;
